import { Stack } from "@mui/material";
import {
  useFoundationColorTokens,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  useQuestionDetailsStore,
  useRankingQuestionV1Store,
} from "../store/hooks";
import { observer } from "mobx-react";
import { CommonChoiceOptionsLayout } from "./CommonChoiceOptionsLayout";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { ResetFieldsButton } from "./commonQuestionConfigurationDetailsComponents/ResetFieldsButton";
import { QuestionOptionsTitleWithTooltip } from "./QuestionOptionsTitleWithTooltip";

interface RankingFieldCustomisationPaneProps {
  setIsResetDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RankingFieldCustomisationPane = observer(
  ({
    setIsResetDialogOpen,
  }: RankingFieldCustomisationPaneProps): React.ReactElement => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const rankingQuestionV1Store = useRankingQuestionV1Store();
    const questionDetailsStore = useQuestionDetailsStore();
    const tokens = useFoundationColorTokens();

    const ResetButton = observer((): React.ReactElement => {
      return (
        <ResetFieldsButton
          isResetDisabled={rankingQuestionV1Store.isResetDisabled}
          setIsResetDialogOpen={setIsResetDialogOpen}
          t={t}
          spacing={spacing}
        />
      );
    });

    const ChoiceCustomisationPane = observer((): React.ReactElement => {
      return (
        <Stack width="600px">
          <QuestionOptionsTitleWithTooltip
            title={t("surveys.choiceCustomisation")}
            spacing={spacing}
            tokens={tokens}
          />
          <Stack spacing={spacing.spaceSM} mt={spacing.spaceLG}>
            {rankingQuestionV1Store.rankingQuestionV1Model.options.map(
              (_field, index) => {
                return (
                  <CommonChoiceOptionsLayout
                    key={index}
                    optionTextWidth="456px"
                    optionCodeWidth="132px"
                    t={t}
                    index={index}
                    spacing={spacing}
                    options={
                      rankingQuestionV1Store.rankingQuestionV1Model.options
                    }
                    isAddNewFieldButtonDisabled={
                      rankingQuestionV1Store.isAddNewFieldButtonDisabled
                    }
                    isDeleteButtonDisabled={
                      rankingQuestionV1Store.isDeleteButtonDisabled
                    }
                    setFieldValidations={
                      questionDetailsStore.setFieldValidations
                    }
                    doesOptionContainErrors={
                      rankingQuestionV1Store.doesOptionContainErrors
                    }
                    addNewField={() =>
                      rankingQuestionV1Store.addNewField(
                        {
                          option: EMPTY_CHARACTER,
                          code: EMPTY_CHARACTER,
                          rank: undefined,
                        },
                        index,
                      )
                    }
                    doesOptionCodeContainErrors={
                      rankingQuestionV1Store.doesOptionCodeContainErrors
                    }
                    isOptionCodeNotUnique={
                      questionDetailsStore.isOptionCodeNotUnique
                    }
                    setDuplicateOptionCode={
                      questionDetailsStore.setDuplicateOptionCode
                    }
                    setCode={rankingQuestionV1Store.setCode}
                    isChevronUpDisabled={
                      rankingQuestionV1Store.isChevronUpDisabled
                    }
                    isChevronDownDisabled={
                      rankingQuestionV1Store.isChevronDownDisabled
                    }
                    moveFieldUp={rankingQuestionV1Store.moveFieldUp}
                    moveFieldDown={rankingQuestionV1Store.moveFieldDown}
                    removeField={rankingQuestionV1Store.removeField}
                    setFieldOptionText={
                      rankingQuestionV1Store.setFieldOptionText
                    }
                  />
                );
              },
            )}
          </Stack>
          <ResetButton />
        </Stack>
      );
    });

    return (
      <Stack gap={spacing.space2XL}>
        <ChoiceCustomisationPane />
      </Stack>
    );
  },
);
